import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../utils/dataInfo';

// Helper function to get nationality display name
const getNationalityDisplay = (nationaliti) => {
    return nationaliti === 'native' ? 'Native' : 'Foreigner';
};

// Helper function to get status name
const getStatusName = (status) => {
    return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to get purpose display name
const getPurposeDisplay = (purpose) => {
    switch(purpose) {
        case 'Tourist': return 'Tourist';
        case 'Business': return 'Business';
        case 'Official': return 'Official';
        default: return purpose || 'N/A';
    }
};

// Helper function to split text into multiple lines for PDF
const splitTextIntoLines = (doc, text, maxWidth) => {
    if (!text) return ['N/A'];
    const words = String(text).split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = doc.getTextWidth(currentLine + ' ' + word);
        if (width < maxWidth) {
            currentLine += ' ' + word;
        } else {
            lines.push(currentLine);
            currentLine = word;
        }
    }
    lines.push(currentLine);
    return lines;
};

// Excel export for Guests (all attributes)
export const exportGuestsToExcel = (guests, filters = {}) => {
    try {
        // Prepare data for Excel - ALL attributes
        const excelData = guests.map((guest, index) => ({
            'No.': index + 1,
            'ID': guest.id,
            'First Name': guest.firstname,
            'Last Name': guest.lastname,
            'Full Name': `${guest.firstname} ${guest.lastname}`,
            'Email': guest.email,
            'Phone': guest.phone,
            'Date of Birth': guest.dob ? new Date(guest.dob).toLocaleDateString() : 'N/A',
            'Profession': guest.profession || 'N/A',
            'Nationality': getNationalityDisplay(guest.nationaliti),
            'National ID': guest.national_id || 'N/A',
            'Foreign Nationality': guest.nationalitycon || 'N/A',
            'Passport No': guest.passport_no || 'N/A',
            'Visa/Reg No': guest.visa_reg_no || 'N/A',
            'Purpose': getPurposeDisplay(guest.purpose),
            'Address': guest.address || 'N/A',
            'Status': getStatusName(guest.status),
            'Created Date': new Date(guest.created_at).toLocaleDateString(),
            'Updated Date': new Date(guest.updated_at).toLocaleDateString()
        }));

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Auto-size columns
        const maxWidth = excelData.reduce((widths, row) => {
            Object.keys(row).forEach((key, idx) => {
                const length = String(row[key]).length;
                if (!widths[idx] || length > widths[idx]) {
                    widths[idx] = length;
                }
            });
            return widths;
        }, []);
        
        worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Guests');

        // Generate Excel file and download
        const fileName = `guests_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Guests Excel export failed:', error);
        throw new Error('Failed to export to Excel: ' + error.message);
    }
};

// PDF export for Guests (only Date, Full Name, Email, Phone, Nationality)
export const exportGuestsToPDF = (guests, filters = {}) => {
    try {
        const doc = new jsPDF();
        
        // Set document properties
        doc.setProperties({
            title: 'Guests Report',
            subject: 'Guests Export',
            author: companyInfo.name,
            creator: companyInfo.name
        });
        
        // Add title
        doc.setFontSize(18);
        doc.setTextColor(33, 33, 33);
        doc.text('GUESTS REPORT', 105, 20, { align: 'center' });
        
        // Add generation details
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 35);
        doc.text(`Total Records: ${guests.length}`, 14, 42);
        
        // Filters applied
        let currentY = 50;
        if (filters.firstname || filters.lastname || filters.email || filters.phone || 
            filters.nationaliti || filters.dateFrom || filters.dateTo || filters.status) {
            let filterText = 'Filters: ';
            const filterParts = [];
            if (filters.firstname) filterParts.push(`First Name: ${filters.firstname}`);
            if (filters.lastname) filterParts.push(`Last Name: ${filters.lastname}`);
            if (filters.email) filterParts.push(`Email: ${filters.email}`);
            if (filters.phone) filterParts.push(`Phone: ${filters.phone}`);
            if (filters.nationaliti) filterParts.push(`Nationality: ${getNationalityDisplay(filters.nationaliti)}`);
            if (filters.status) filterParts.push(`Status: ${getStatusName(filters.status)}`);
            if (filters.dateFrom) filterParts.push(`From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
            if (filters.dateTo) filterParts.push(`To: ${new Date(filters.dateTo).toLocaleDateString()}`);
            filterText += filterParts.join(', ');
            
            doc.text(filterText, 14, currentY);
            currentY += 8;
        }
        
        // Draw table header
        const pageWidth = doc.internal.pageSize.width;
        const margin = 14;
        const rowHeight = 10;
        
        // Column widths for the 5 columns: Date, Full Name, Email, Phone, Nationality
        const colWidths = [25, 60, 60, 40, 30];
        
        // Check if we need a new page
        if (currentY + rowHeight + (guests.length * rowHeight) > doc.internal.pageSize.height - 20) {
            doc.addPage();
            currentY = margin;
        }
        
        // Draw table header
        doc.setFillColor(63, 81, 181);
        doc.setTextColor(255, 255, 255);
        doc.setFont(undefined, 'bold');
        
        const headers = ['Date', 'Full Name', 'Email', 'Phone'];
        let xPos = margin;
        
        // Draw header background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        // Draw header text
        headers.forEach((header, index) => {
            const cellX = xPos + 2;
            doc.text(header, cellX, currentY + 7);
            xPos += colWidths[index];
        });
        
        currentY += rowHeight;
        
        // Draw table rows
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
        
        guests.forEach((guest, rowIndex) => {
            // Check for page break
            if (currentY + rowHeight > doc.internal.pageSize.height - 20) {
                doc.addPage();
                currentY = margin;
                
                // Redraw header on new page
                doc.setFillColor(63, 81, 181);
                doc.setTextColor(255, 255, 255);
                doc.setFont(undefined, 'bold');
                
                doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
                
                xPos = margin;
                headers.forEach((header, index) => {
                    doc.text(header, xPos + 2, currentY + 7);
                    xPos += colWidths[index];
                });
                
                currentY += rowHeight;
                doc.setFont(undefined, 'normal');
                doc.setTextColor(0, 0, 0);
            }
            
            // Alternate row colors
            if (rowIndex % 2 === 0) {
                doc.setFillColor(248, 248, 248);
            } else {
                doc.setFillColor(255, 255, 255);
            }
            
            // Draw row background
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            // Draw row data
            xPos = margin;
            
            // Date column
            const date = new Date(guest.created_at).toLocaleDateString();
            doc.text(date, xPos + 2, currentY + 7);
            xPos += colWidths[0];
            
            // Full Name column
            const fullName = `${guest.firstname} ${guest.lastname}`;
            const nameLines = splitTextIntoLines(doc, fullName, colWidths[1] - 4);
            nameLines.forEach((line, lineIndex) => {
                if (lineIndex === 0) {
                    doc.text(line, xPos + 2, currentY + 7);
                } else if (lineIndex === 1) {
                    doc.text(line, xPos + 2, currentY + 12);
                }
            });
            xPos += colWidths[1];
            
            // Email column (prevent overlapping)
            const email = guest.email || 'N/A';
            const emailLines = splitTextIntoLines(doc, email, colWidths[2] - 4);
            emailLines.forEach((line, lineIndex) => {
                if (lineIndex === 0) {
                    doc.text(line, xPos + 2, currentY + 7);
                } else if (lineIndex === 1) {
                    doc.text(line, xPos + 2, currentY + 12);
                }
            });
            xPos += colWidths[2];
            
            // Phone column
            const phone = guest.phone || 'N/A';
            doc.text(phone, xPos + 2, currentY + 7);
            xPos += colWidths[3];

        });
        
        const fileName = `guests_report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Guests PDF export failed:', error);
        throw new Error('Failed to export to PDF: ' + error.message);
    }
};

// CSV export for Guests (all attributes)
export const exportGuestsToCSV = (guests) => {
    try {
        const headers = [
            'No.', 'ID', 'First Name', 'Last Name', 'Full Name', 'Email', 'Phone', 
            'Date of Birth', 'Profession', 'Nationality', 'National ID', 
            'Foreign Nationality', 'Passport No', 'Visa/Reg No', 'Purpose', 
            'Address', 'Status', 'Created Date', 'Updated Date'
        ];
        
        const csvData = guests.map((guest, index) => [
            index + 1,
            guest.id,
            `"${(guest.firstname || '').replace(/"/g, '""')}"`,
            `"${(guest.lastname || '').replace(/"/g, '""')}"`,
            `"${(guest.firstname || '')} ${(guest.lastname || '')}".replace(/"/g, '""')"`,
            `"${(guest.email || '').replace(/"/g, '""')}"`,
            `"${(guest.phone || '').replace(/"/g, '""')}"`,
            `"${guest.dob ? new Date(guest.dob).toLocaleDateString() : 'N/A'}"`,
            `"${(guest.profession || '').replace(/"/g, '""')}"`,
            `"${getNationalityDisplay(guest.nationaliti).replace(/"/g, '""')}"`,
            `"${(guest.national_id || '').replace(/"/g, '""')}"`,
            `"${(guest.nationalitycon || '').replace(/"/g, '""')}"`,
            `"${(guest.passport_no || '').replace(/"/g, '""')}"`,
            `"${(guest.visa_reg_no || '').replace(/"/g, '""')}"`,
            `"${getPurposeDisplay(guest.purpose).replace(/"/g, '""')}"`,
            `"${(guest.address || '').replace(/"/g, '""')}"`,
            `"${getStatusName(guest.status).replace(/"/g, '""')}"`,
            `"${new Date(guest.created_at).toLocaleDateString()}"`,
            `"${new Date(guest.updated_at).toLocaleDateString()}"`
        ]);

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `guests_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return { success: true };
    } catch (error) {
        console.error('Guests CSV export failed:', error);
        throw new Error('Failed to export to CSV: ' + error.message);
    }
};